import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { usePageMeta, organizationSchema, serviceSchema, faqSchema, breadcrumbSchema } from '../lib/seo';
import { siteConfig } from '../siteConfig';
import { customSoftwareFaqs, customSoftwareServices, softwareTestimonials } from '../data/siteData';
import {
  ButtonLink,
  Card,
  Container,
  CTASection,
  FAQList,
  FeatureList,
  PageHero,
  Section,
  SectionHeading,
} from '../components/SiteUi';

const Proof = () => (
  <div>
    <div className="grid gap-4 md:grid-cols-3">
      {softwareTestimonials.map((item) => (
        <Card key={item.author}>
          <p className="text-sm leading-6 text-dust">“{item.quote}”</p>
          <p className="mt-4 text-sm font-medium text-cream">{item.author}</p>
          <p className="text-xs text-ash">{item.role}</p>
        </Card>
      ))}
    </div>
    {siteConfig.upwork ? (
      <div className="mt-5">
        <ButtonLink href={siteConfig.upwork} variant="secondary" target="_blank" rel="noopener noreferrer">See the Upwork profile</ButtonLink>
      </div>
    ) : null}
  </div>
);

export function CustomSoftwarePage() {
  usePageMeta({
    title: 'Custom Software, MVP and SaaS Development – CloudNext',
    description: 'Australian software studio building custom apps, MVPs and SaaS platforms. Fixed-fee delivery and 20+ years of experience.',
    path: '/custom-software',
    jsonLd: [
      organizationSchema(),
      serviceSchema({ name: 'Custom Software Development', serviceType: 'Custom software, MVP, SaaS and mobile app development', path: '/custom-software', description: 'Custom software, MVP, SaaS and mobile app development for Australian businesses.' }),
      faqSchema(customSoftwareFaqs),
      breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Custom Software', path: '/custom-software' }]),
    ],
  });

  return (
    <>
      <PageHero
        eyebrow="Custom software"
        title={<>Software built around <span className="text-gold-gradient">how the business works.</span></>}
        description="Custom web and mobile apps, MVPs, SaaS platforms and AI integration. For businesses with a real workflow problem, and founders validating an idea."
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'Custom Software' }]}
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <ButtonLink to="/contact">Talk about a project</ButtonLink>
          <ButtonLink to="/work" variant="secondary">See the work</ButtonLink>
        </div>
      </PageHero>

      <Section>
        <Container>
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <SectionHeading eyebrow="Who it is for" title="Built for your workflow, not a template." />
              <FeatureList
                className="mt-6"
                items={[
                  'Businesses replacing spreadsheets, email and manual processes',
                  'Founders validating a new product idea',
                  'Companies that need a customer or staff portal',
                  'Teams that need an app for field or mobile work',
                  'Businesses adding AI to an existing product',
                ]}
              />
            </div>
            <Card>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-gold-light">How we deliver</p>
              <FeatureList
                className="mt-4"
                items={[
                  'Discovery and a written scope',
                  'Fixed fee for defined work',
                  'Direct communication with the builder',
                  'You see progress at each stage',
                  'Support and maintenance after launch',
                ]}
              />
            </Card>
          </div>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <SectionHeading eyebrow="Services" title="What we build." />
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {customSoftwareServices.map((service) => (
              <Card key={service.slug}>
                <h3 className="font-display text-xl text-cream">{service.name}</h3>
                <p className="mt-2 text-sm leading-6 text-dust">{service.description}</p>
                <ButtonLink to={`/${service.slug}`} variant="ghost" size="sm" className="mt-4 px-0">Learn more →</ButtonLink>
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeading eyebrow="Proof" title="Custom software work." description="These are Upwork reviews for Orfeo's custom software work, not reviews of the AI receptionist service." />
          <div className="mt-10"><Proof /></div>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <SectionHeading eyebrow="Pricing" title="Fixed-fee scoping." description="The existing pricing guide uses indicative ranges. The scope and quote are agreed before work starts." />
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              { name: 'Focused build', price: 'From $15K', text: 'A defined app, portal or MVP with a clear scope.' },
              { name: 'Larger platform', price: 'From $50K', text: 'Multi-feature SaaS, connections to other software or complex workflows.' },
              { name: 'Ongoing support', price: 'Retainer', text: 'Maintenance, changes and support after launch.' },
            ].map((item) => (
              <Card key={item.name}>
                <h3 className="font-display text-lg text-cream">{item.name}</h3>
                <p className="mt-2 font-display text-xl text-gold-light">{item.price}</p>
                <p className="mt-2 text-sm leading-6 text-dust">{item.text}</p>
              </Card>
            ))}
          </div>
          <p className="mt-5 text-sm leading-6 text-ash">Indicative only. The final quote depends on scope, connections and timeline.</p>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeading eyebrow="Questions" title="Custom software questions." />
          <div className="mt-8"><FAQList items={customSoftwareFaqs} /></div>
        </Container>
      </Section>

      <CTASection
        title="Tell us what the software needs to do."
        description="Bring the workflow, the problem or the idea. We will tell you what is realistic, what it takes and what it costs."
        primaryTo="/contact"
        primaryLabel="Talk about a project"
        secondaryTo="/work"
        secondaryLabel="See the work"
      />
    </>
  );
}

export function CustomSoftwareServicePage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const service = customSoftwareServices.find((item) => item.slug === slug);
  const path = service ? `/${service.slug}` : '/404';
  const faqs = service ? service.faqs : [];

  usePageMeta({
    title: service ? `${service.title} – CloudNext` : 'Page not found – CloudNext',
    description: service ? service.description : 'Page not found.',
    path,
    jsonLd: service
      ? [
        organizationSchema(),
        serviceSchema({ name: service.name, serviceType: service.name, path, description: service.description, audience: service.audience }),
        faqSchema(faqs),
        breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Custom Software', path: '/custom-software' }, { name: service.name, path }]),
      ]
      : [organizationSchema()],
  });

  if (!service) return <Navigate to="/404" replace />;

  return (
    <>
      <PageHero
        eyebrow={service.name}
        title={service.title}
        description={service.description}
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'Custom Software', to: '/custom-software' }, { name: service.name }]}
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <ButtonLink to="/contact">Talk about a project</ButtonLink>
          <ButtonLink to="/custom-software" variant="secondary">See all software services</ButtonLink>
        </div>
      </PageHero>

      <Section>
        <Container>
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <SectionHeading eyebrow="What is included" title="The work, not just the code." />
              <FeatureList className="mt-6" items={service.bullets} />
            </div>
            <Card>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-gold-light">Who it is for</p>
              <p className="mt-3 text-sm leading-6 text-dust">{service.audience}</p>
              <p className="mt-6 font-mono-accent text-[10px] uppercase tracking-[0.25em] text-gold-light">How it starts</p>
              <p className="mt-3 text-sm leading-6 text-dust">A short discovery call, a written scope and a fixed-fee quote. No surprise hourly billing.</p>
            </Card>
          </div>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <SectionHeading eyebrow="Proof" title="Custom software work." description="Upwork reviews for Orfeo's custom software work, not the AI receptionist service." />
          <div className="mt-10"><Proof /></div>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeading eyebrow="Questions" title={`${service.name} questions.`} />
          <div className="mt-8"><FAQList items={faqs} /></div>
        </Container>
      </Section>

      <CTASection
        title={`Talk about ${service.name.toLowerCase()}.`}
        description="Bring the workflow or the idea. We will tell you what is realistic and what it takes."
        primaryTo="/contact"
        primaryLabel="Talk about a project"
        secondaryTo="/custom-software"
        secondaryLabel="See all software services"
      />
    </>
  );
}
